import { useState, useRef, useEffect } from "react";
import { cn } from "../../utils";
import { DateRangeKey, DATE_RANGE_LABELS } from "../../utils/dateRanges";

interface DateRangePickerPopoverProps {
  value: DateRangeKey;
  onSelect: (key: DateRangeKey) => void;
  customStartDate?: string;
  customEndDate?: string;
  onCustomApply: (start: string, end: string) => void;
}

const QUICK_SHORTCUTS: DateRangeKey[] = [
  "today",
  "yesterday",
  "previous_7_days",
  "previous_30_days",
];

const PREVIOUS_OPTIONS: DateRangeKey[] = [
  "previous_week",
  "previous_month",
  "previous_year",
];

const THIS_OPTIONS: DateRangeKey[] = ["this_week", "this_month", "this_year"];

function ShortcutButton({
  rangeKey,
  isSelected,
  onClick,
}: {
  rangeKey: DateRangeKey;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
        isSelected
          ? "border-brand-200 bg-brand-50 text-brand-600 dark:border-brand-700 dark:bg-brand-500/10 dark:text-brand-400"
          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
      )}
    >
      {DATE_RANGE_LABELS[rangeKey]}
    </button>
  );
}

export default function DateRangePickerPopover({
  value,
  onSelect,
  customStartDate = "",
  customEndDate = "",
  onCustomApply,
}: DateRangePickerPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localStart, setLocalStart] = useState(customStartDate);
  const [localEnd, setLocalEnd] = useState(customEndDate);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalStart(customStartDate);
    setLocalEnd(customEndDate);
  }, [customStartDate, customEndDate]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handlePresetClick(key: DateRangeKey) {
    onSelect(key);
    setIsOpen(false);
  }

  function handleCustomApply() {
    if (localStart && localEnd) {
      onCustomApply(localStart, localEnd);
      setIsOpen(false);
    }
  }

  const displayLabel =
    value === "custom" && customStartDate && customEndDate
      ? `${customStartDate} - ${customEndDate}`
      : DATE_RANGE_LABELS[value];

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
      >
        <svg
          className="h-4 w-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span>{displayLabel}</span>
        <svg
          className={cn(
            "h-4 w-4 text-gray-400 transition-transform",
            isOpen && "rotate-180"
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Popover */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {/* Quick shortcuts — 2×2 grid */}
          <div className="grid grid-cols-2 gap-2">
            {QUICK_SHORTCUTS.map((key) => (
              <ShortcutButton
                key={key}
                rangeKey={key}
                isSelected={value === key}
                onClick={() => handlePresetClick(key)}
              />
            ))}
          </div>

          {/* Previous section */}
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Previous
            </p>
            <div className="grid grid-cols-3 gap-2">
              {PREVIOUS_OPTIONS.map((key) => (
                <ShortcutButton
                  key={key}
                  rangeKey={key}
                  isSelected={value === key}
                  onClick={() => handlePresetClick(key)}
                />
              ))}
            </div>
          </div>

          {/* This section */}
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              This
            </p>
            <div className="grid grid-cols-3 gap-2">
              {THIS_OPTIONS.map((key) => (
                <ShortcutButton
                  key={key}
                  rangeKey={key}
                  isSelected={value === key}
                  onClick={() => handlePresetClick(key)}
                />
              ))}
            </div>
          </div>

          {/* Separator */}
          <hr className="my-4 border-gray-200 dark:border-gray-700" />

          {/* Custom range */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Custom Range
            </p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={localStart}
                onChange={(e) => setLocalStart(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
              />
              <span className="text-gray-400">-</span>
              <input
                type="date"
                value={localEnd}
                onChange={(e) => setLocalEnd(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
              />
            </div>
            <button
              type="button"
              onClick={handleCustomApply}
              disabled={!localStart || !localEnd}
              className="mt-2 w-full rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
